import './style.css';
import { LoadingComponent } from '../../controls';
import { useClientSettingsReducer } from './useClientSettingsReducer';
import { ClientSettings } from '../../types/clientSettings';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import React, { useState, useEffect } from 'react';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import toast from 'react-hot-toast';

const ClientSettingsPage = () => {
  const { data, addRecord, update, deleteRecord } = useClientSettingsReducer();
  const [localDataMap, setLocalDataMap] = useState<Map<number, ClientSettings>>(new Map());

  useEffect(() => {
    const newMap = new Map<number, ClientSettings>();
    data.Records.forEach((record) => {
      const existing = localDataMap.get(record.id);
      if (existing && existing.id === record.id && record.id !== 0) {
        const hasLocalChanges = 
          existing.mask !== record.mask ||
          existing.metadata.devBuild !== record.metadata.devBuild ||
          existing.metadata.firstBoosterTutorialLevel !== record.metadata.firstBoosterTutorialLevel ||
          existing.metadata.firstPurchaseTutorialLevel !== record.metadata.firstPurchaseTutorialLevel ||
          existing.metadata.challengeTutorialLevel !== record.metadata.challengeTutorialLevel ||
          existing.metadata.noAds !== record.metadata.noAds ||
          existing.metadata.adsFirstLevel !== record.metadata.adsFirstLevel ||
          existing.metadata.interstitialEveryLevel !== record.metadata.interstitialEveryLevel ||
          existing.metadata.noPurchase !== record.metadata.noPurchase ||
          existing.metadata.dailyBonusEnabled !== record.metadata.dailyBonusEnabled ||
          existing.metadata.dailyBonusRewardAmount !== record.metadata.dailyBonusRewardAmount ||
          existing.metadata.wordDescriptionEnabled !== record.metadata.wordDescriptionEnabled ||
          existing.metadata.missedWordReportEnabled !== record.metadata.missedWordReportEnabled;
        if (hasLocalChanges) {
          newMap.set(record.id, existing);
        } else {
          newMap.set(record.id, record);
        }
      } else {
        newMap.set(record.id, record);
      }
    });
    setLocalDataMap(newMap);
  }, [data.Records]);

  if (data.Loading && data.Records.length === 0) {
    return <LoadingComponent />;
  }

  const sortedRecords = [...data.Records].sort((a, b) => {
    if (a.mask === null) return -1;
    if (b.mask === null) return 1;
    return (a.mask || '').localeCompare(b.mask || '');
  });

  const hasChanges = (id: number): boolean => {
    const original = data.Records.find((r) => r.id === id);
    const local = localDataMap.get(id);
    if (!original || !local) return false;
    
    const originalMask = original.mask?.trim() || null;
    const localMask = local.mask?.trim() || null;
    if (originalMask !== localMask) return true;
    if (original.metadata.devBuild !== local.metadata.devBuild) return true;
    if (original.metadata.firstBoosterTutorialLevel !== local.metadata.firstBoosterTutorialLevel) return true;
    if (original.metadata.firstPurchaseTutorialLevel !== local.metadata.firstPurchaseTutorialLevel) return true;
    if (original.metadata.challengeTutorialLevel !== local.metadata.challengeTutorialLevel) return true;
    if (original.metadata.noAds !== local.metadata.noAds) return true;
    if (original.metadata.adsFirstLevel !== local.metadata.adsFirstLevel) return true;
    if (original.metadata.interstitialEveryLevel !== local.metadata.interstitialEveryLevel) return true;
    if (original.metadata.noPurchase !== local.metadata.noPurchase) return true;
    if (original.metadata.dailyBonusEnabled !== local.metadata.dailyBonusEnabled) return true;
    if (original.metadata.dailyBonusRewardAmount !== local.metadata.dailyBonusRewardAmount) return true;
    if (original.metadata.wordDescriptionEnabled !== local.metadata.wordDescriptionEnabled) return true;
    if (original.metadata.missedWordReportEnabled !== local.metadata.missedWordReportEnabled) return true;
    
    return false;
  };

  const handleSave = async (clientSettings: ClientSettings) => {
    const original = data.Records.find((r) => r.id === clientSettings.id);
    const isSystemSettings = original?.mask === null;
    if (isSystemSettings && clientSettings.mask !== null && clientSettings.mask.trim() !== '') {
      toast.error('Cannot assign Mask to system ClientSettings');
      setLocalData(clientSettings.id, original!);
      return;
    }
    if (clientSettings.id === 0 && (!clientSettings.mask || clientSettings.mask.trim() === '')) {
      toast.error('Mask is required');
      return;
    }
    const isNew = clientSettings.id === 0;
    const settingsToSave = isSystemSettings ? { ...clientSettings, mask: null } : clientSettings;
    const success = await update(settingsToSave);
    if (success && isNew) {
      setLocalDataMap((prev) => {
        const next = new Map(prev);
        next.delete(0);
        return next;
      });
    }
  };

  const handleCancel = (id: number) => {
    const original = data.Records.find((r) => r.id === id);
    if (original) {
      setLocalData(id, original);
    }
  };

  const handleDelete = (clientSettings: ClientSettings) => {
    if (clientSettings.mask === null) {
      confirmDialog({
        message: 'Cannot delete system client settings',
        header: 'Delete Error',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        accept: () => {},
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to delete client settings for Mask ${clientSettings.mask}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        await deleteRecord(clientSettings.id);
      },
    });
  };

  const getLocalData = (id: number): ClientSettings => {
    return localDataMap.get(id) || data.Records.find((r) => r.id === id)!;
  };

  const setLocalData = (id: number, data: ClientSettings) => {
    setLocalDataMap((prev) => {
      const next = new Map(prev);
      next.set(id, data);
      return next;
    });
  };

  const renderCard = (clientSettings: ClientSettings) => {
    const isSystem = clientSettings.mask === null;
    const localData = getLocalData(clientSettings.id);
    const hasUnsavedChanges = hasChanges(clientSettings.id);
    const isNew = clientSettings.id === 0;

    return (
      <Card
        key={clientSettings.id}
        className={`client-settings-card ${isSystem ? 'system-card' : ''}`}
      >
        <div className="client-settings-card-header">
          <div className="client-settings-card-title">
            {isSystem && <i className="pi pi-lock"></i>}
            <span>
              {isSystem ? 'System Settings' : `Mask: ${clientSettings.mask || localData.mask || ''}`}
            </span>
          </div>
          <div className="client-settings-card-actions">
            {!isSystem && !hasUnsavedChanges && !isNew && (
              <Button
                icon="pi pi-trash"
                onClick={() => handleDelete(clientSettings)}
                rounded
                text
                size="small"
                severity="danger"
              />
            )}
          </div>
        </div>

        <div className="client-settings-card-content">
          <div className="client-settings-field">
            <label htmlFor={`mask-${clientSettings.id}`}>Mask</label>
            <InputText
              id={`mask-${clientSettings.id}`}
              value={isSystem ? '*' : localData.mask || ''}
              onChange={(e) => {
                if (isSystem) return;
                setLocalData(clientSettings.id, { ...localData, mask: e.target.value || null });
              }}
              placeholder="e.g. ios*, *1.1.14*, *_spicy*"
              className="w-full"
              disabled={isSystem}
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`devBuild-${clientSettings.id}`}>Dev Build</label>
            <Checkbox
              inputId={`devBuild-${clientSettings.id}`}
              checked={localData.metadata.devBuild}
              onChange={(e) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    devBuild: e.checked || false,
                  },
                })
              }
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`firstBoosterTutorialLevel-${clientSettings.id}`}>
              First Booster Tutorial Level
            </label>
            <InputNumber
              id={`firstBoosterTutorialLevel-${clientSettings.id}`}
              value={localData.metadata.firstBoosterTutorialLevel}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    firstBoosterTutorialLevel: e.value || 0,
                  },
                })
              }
              min={0}
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`firstPurchaseTutorialLevel-${clientSettings.id}`}>
              First Purchase Tutorial Level
            </label>
            <InputNumber
              id={`firstPurchaseTutorialLevel-${clientSettings.id}`}
              value={localData.metadata.firstPurchaseTutorialLevel}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    firstPurchaseTutorialLevel: e.value || 0,
                  },
                })
              }
              min={0}
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`challengeTutorialLevel-${clientSettings.id}`}>
              Challenge Tutorial Level
            </label>
            <InputNumber
              id={`challengeTutorialLevel-${clientSettings.id}`}
              value={localData.metadata.challengeTutorialLevel}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    challengeTutorialLevel: e.value || 0,
                  },
                })
              }
              min={0}
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`noAds-${clientSettings.id}`}>No Ads</label>
            <Checkbox
              inputId={`noAds-${clientSettings.id}`}
              checked={localData.metadata.noAds}
              onChange={(e) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    noAds: e.checked || false,
                  },
                })
              }
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`adsFirstLevel-${clientSettings.id}`}>
              Ads First Level
            </label>
            <InputNumber
              id={`adsFirstLevel-${clientSettings.id}`}
              value={localData.metadata.adsFirstLevel}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    adsFirstLevel: e.value || 0,
                  },
                })
              }
              min={0}
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`interstitialEveryLevel-${clientSettings.id}`}>
              Interstitial Every Level
            </label>
            <InputNumber
              id={`interstitialEveryLevel-${clientSettings.id}`}
              value={localData.metadata.interstitialEveryLevel}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    interstitialEveryLevel: e.value || 0,
                  },
                })
              }
              min={0}
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`noPurchase-${clientSettings.id}`}>No Purchase</label>
            <Checkbox
              inputId={`noPurchase-${clientSettings.id}`}
              checked={localData.metadata.noPurchase}
              onChange={(e) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    noPurchase: e.checked || false,
                  },
                })
              }
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`dailyBonusEnabled-${clientSettings.id}`}>Daily Bonus Enabled</label>
            <Checkbox
              inputId={`dailyBonusEnabled-${clientSettings.id}`}
              checked={localData.metadata.dailyBonusEnabled}
              onChange={(e) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    dailyBonusEnabled: e.checked || false,
                  },
                })
              }
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`dailyBonusRewardAmount-${clientSettings.id}`}>
              Daily Bonus Reward Amount
            </label>
            <InputNumber
              id={`dailyBonusRewardAmount-${clientSettings.id}`}
              value={localData.metadata.dailyBonusRewardAmount}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    dailyBonusRewardAmount: e.value || 0,
                  },
                })
              }
              min={0}
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`wordDescriptionEnabled-${clientSettings.id}`}>Word Description Enabled</label>
            <Checkbox
              inputId={`wordDescriptionEnabled-${clientSettings.id}`}
              checked={localData.metadata.wordDescriptionEnabled}
              onChange={(e) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    wordDescriptionEnabled: e.checked || false,
                  },
                })
              }
            />
          </div>

          <div className="client-settings-field client-settings-field-row">
            <label htmlFor={`missedWordReportEnabled-${clientSettings.id}`}>Missed Word Report Enabled</label>
            <Checkbox
              inputId={`missedWordReportEnabled-${clientSettings.id}`}
              checked={localData.metadata.missedWordReportEnabled}
              onChange={(e) =>
                setLocalData(clientSettings.id, {
                  ...localData,
                  metadata: {
                    ...localData.metadata,
                    missedWordReportEnabled: e.checked || false,
                  },
                })
              }
            />
          </div>

          {(isNew || hasUnsavedChanges) && (
            <div className="flex gap-2 mt-2">
              <Button
                label="Save"
                icon="pi pi-check"
                onClick={() => handleSave(localData)}
                size="small"
                className="flex-1"
                disabled={isNew && !localData.mask}
              />
              <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={() => {
                  if (isNew) {
                    deleteRecord(0);
                  } else {
                    handleCancel(clientSettings.id);
                  }
                }}
                size="small"
                severity="secondary"
                className="flex-1"
              />
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="page-client-settings p-3">
      <ConfirmDialog />
      <Toolbar
        start={
          <Button
            onClick={addRecord}
            label="+ Add"
            size="small"
            disabled={data.Records.some((r) => r.id === 0)}
          />
        }
      />

      {sortedRecords.length > 0 && (
        <div className="client-settings-grid mt-3">
          {sortedRecords.map((record) => renderCard(record))}
        </div>
      )}
    </div>
  );
};

export default ClientSettingsPage;
